import type { Action, Location } from 'history'
import { useCallback, useEffect, useRef, useState } from 'react'
import { history } from 'store'

// The blocked transition, captured so proceed() can replay it with its original
// semantics (a browser Back is a POP, not a PUSH).
type PendingTransition = { location: Location; action: Action }

export type UseUnsavedChangesGuard = {
  // A blocked in-app navigation is pending — the scene should show the
  // 3-action unsaved-changes dialog (FR-43).
  isPromptOpen: boolean
  // Resolve the blocked navigation: tear the block down and complete the
  // originally-requested transition. Call after discarding edits or a
  // successful publish.
  proceed: () => void
  // Dismiss the dialog and stay on the page ("Keep editing").
  cancel: () => void
}

// FR-43 unsaved-changes guard. Two complementary channels while `isDirty`:
//
//   1. history.block() intercepts IN-APP route navigation (react-router v4 has
//      no <Prompt>-with-custom-UI, so we block the app's shared browser history
//      — the same instance the router uses, per useLegalDocsBlock's precedent —
//      capture the attempted location, and surface a custom 3-action dialog
//      instead of window.confirm). Returning `false` from the prompt cancels the
//      transition; the scene then drives the outcome via proceed()/cancel().
//   2. a `beforeunload` listener covers a browser refresh / tab close, which
//      history.block can't see (mirrors useUnsavedChangesPrompt).
//
// Both are registered only while dirty and torn down when clean or on unmount.
export function useUnsavedChangesGuard(
  isDirty: boolean
): UseUnsavedChangesGuard {
  const [pending, setPending] = useState<PendingTransition | null>(null)
  // Holds history.block's teardown so proceed() can remove the block
  // synchronously before pushing (otherwise the push would be re-blocked).
  const unblockRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (!isDirty) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Spec-compliant native "leave site?" trigger. The browser shows its own
      // copy (custom strings were removed from the spec), so there's no i18n to
      // thread here — the in-app dialog carries the translated messaging.
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    const unblock = history.block((location, action) => {
      setPending({ location, action })
      // Returning false cancels the transition; the dialog decides what
      // happens next (proceed / cancel).
      return false
    })
    unblockRef.current = unblock

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      unblock()
      unblockRef.current = null
    }
  }, [isDirty])

  const proceed = useCallback(() => {
    const target = pending
    setPending(null)
    // Remove the block first so the replayed navigation isn't intercepted again.
    unblockRef.current?.()
    unblockRef.current = null
    if (!target) return
    const { pathname, search, hash, state } = target.location
    const path = `${pathname}${search}${hash}`
    // A REPLACE is replayed as a replace so it doesn't add an entry. PUSH and
    // POP both resume by navigating to the captured target: history v4's block
    // API exposes no delta, so a POP can't be reversed exactly (back vs forward
    // vs go(n)) — navigating to the target always lands the merchant on the
    // page they were headed to, which is safer than guessing a direction with
    // goBack(). The one cost is a POP leaving an extra forward entry. The
    // original location.state is carried through so stateful transitions aren't
    // dropped on confirmation.
    if (target.action === 'REPLACE') {
      history.replace(path, state)
    } else {
      history.push(path, state)
    }
  }, [pending])

  const cancel = useCallback(() => {
    setPending(null)
  }, [])

  return { isPromptOpen: pending !== null, proceed, cancel }
}
